import React, { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "../supabaseClient";
import { useTheme } from "./theme/ThemeContext.jsx";

const CURRENT_PLAYOFF_YEAR = new Date().getFullYear();
const OT_LEGACY_ITEM_PREFIX = "otplay:";

const toISODate = (date = new Date()) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const toESPNDate = (date = new Date()) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}${m}${d}`;
};

const addDays = (date, amount) => {
  const next = new Date(date);
  next.setDate(next.getDate() + amount);
  return next;
};

const getRoundKeyFromSeriesId = (seriesId) => {
  const parts = String(seriesId || "").split("-").filter(Boolean);
  if (parts.length < 3) return "";
  return String(parts[0] || "").toUpperCase();
};

const getRoundOrder = (roundKey) => {
  const key = String(roundKey || "").toUpperCase();
  if (!key) return 999;

  if (key === "RD16" || key === "R1") return 1;
  if (key === "RD8" || key === "R2" || key === "QF") return 2;
  if (key === "RD4" || key === "R3" || key === "SF" || key === "CF") return 3;
  if (key === "F" || key === "SCF" || key === "FINAL") return 4;

  return 999;
};

const getRoundLabel = (roundKey) => {
  const key = String(roundKey || "").toUpperCase();
  const order = getRoundOrder(key);
  if (order <= 4) return `Round ${order}`;
  return key || "Round";
};

const isUnresolvedTeamSlot = (teamName) => {
  const value = String(teamName || "").trim();
  if (!value) return true;
  return value.includes("/") || /\bTBD\b/i.test(value);
};

const toTeamButtonLabel = (teamName) => (isUnresolvedTeamSlot(teamName) ? "TBD" : teamName);

const isPickInCurrentPlayoffYear = (pick) => {
  const stamp = pick?.pick_date || pick?.created_at || "";
  const year = new Date(stamp).getFullYear();
  return year === CURRENT_PLAYOFF_YEAR;
};

const normalizeSeriesKey = (seriesId) => {
  const raw = String(seriesId || "").trim();
  if (!raw) return "";

  const parts = raw.split("-").filter(Boolean);
  if (parts.length < 2) {
    return raw.toUpperCase();
  }

  if (parts.length === 2) {
    const [a, b] = parts;
    const isNumericPair = /^\d+$/.test(a) && /^\d+$/.test(b);
    if (isNumericPair) {
      return [a, b]
        .sort((x, y) => Number(x) - Number(y))
        .join("-");
    }

    return [a.toUpperCase(), b.toUpperCase()].sort().join("-");
  }

  const tail = parts.slice(1);
  if (tail.length === 2) {
    const [a, b] = tail;
    const isNumericPair = /^\d+$/.test(a) && /^\d+$/.test(b);
    if (isNumericPair) {
      return [a, b]
        .sort((x, y) => Number(x) - Number(y))
        .join("-");
    }

    return [a.toUpperCase(), b.toUpperCase()].sort().join("-");
  }

  return tail.join("-").toUpperCase();
};

const inferWinnerFromSummary = (summary, candidates) => {
  const text = String(summary || "").toUpperCase();
  if (!text) return null;

  const hasSeriesWinKeyword =
    /\b(WON|WINS)\b/.test(text) &&
    (/\bSERIES\b/.test(text) || /\bADVANCE(S|D)?\b/.test(text));
  if (!hasSeriesWinKeyword) return null;

  const winnerSegment = text.split(/\bWON\b|\bWINS\b/)[0]?.trim() || "";

  const normalizeToken = (token) => String(token || "").trim().toUpperCase();
  const buildTokens = (candidate) => {
    const team = candidate?.team || {};
    return [
      candidate?.abbrev,
      team.abbreviation,
      team.displayName,
      team.shortDisplayName,
      `${team.location || ""} ${team.name || ""}`.trim(),
      team.location,
      team.name,
    ]
      .map(normalizeToken)
      .filter(Boolean);
  };

  const matchesToken = (haystack, token) => {
    if (!token) return false;
    if (token.length <= 4) {
      return new RegExp(`(^|\\W)${token}($|\\W)`, "i").test(haystack);
    }
    return haystack.includes(token);
  };

  for (const candidate of candidates || []) {
    const tokens = buildTokens(candidate);
    if (tokens.some((token) => matchesToken(winnerSegment, token))) {
      return candidate?.abbrev || null;
    }
  }

  for (const candidate of candidates || []) {
    const tokens = buildTokens(candidate);
    if (tokens.some((token) => matchesToken(text, token))) {
      return candidate?.abbrev || null;
    }
  }

  return null;
};

const buildGameWinnerMap = (games) => {
  const map = {};
  games.forEach((game) => {
    const home = Number(game.homeTeam?.score || 0);
    const away = Number(game.awayTeam?.score || 0);
    const state = game.gameState;
    const isFinished =
      state === "FINAL" ||
      state === "OFF" ||
      (game.clock?.timeRemaining === "00:00" && Number(game.period || 0) >= 3);

    if (!isFinished) {
      return;
    }

    if (home > away) {
      map[String(game.id)] = game.homeTeam?.abbrev;
    } else if (away > home) {
      map[String(game.id)] = game.awayTeam?.abbrev;
    } else {
      map[String(game.id)] = "TIE";
    }
  });
  return map;
};

const fetchGameResultFromApi = async (gameId) => {
  const response = await fetch(
    `https://friendly-bets-back-end.vercel.app/api/gamecenter/${gameId}/play-by-play`
  );

  if (!response.ok) {
    throw new Error(`Failed fetching game result for ${gameId}`);
  }

  const game = await response.json();
  const home = Number(game.homeTeam?.score || 0);
  const away = Number(game.awayTeam?.score || 0);
  const state = game.gameState;
  const isFinished =
    state === "OFF" ||
    state === "FINAL" ||
    (game.clock?.timeRemaining === "00:00" && Number(game.period || 0) >= 3);

  if (!isFinished) {
    return {
      winner: null,
      homeAbbrev: game.homeTeam?.abbrev || "",
      awayAbbrev: game.awayTeam?.abbrev || "",
      state,
    };
  }

  if (home > away) {
    return {
      winner: game.homeTeam?.abbrev || null,
      homeAbbrev: game.homeTeam?.abbrev || "",
      awayAbbrev: game.awayTeam?.abbrev || "",
      state,
    };
  }

  if (away > home) {
    return {
      winner: game.awayTeam?.abbrev || null,
      homeAbbrev: game.homeTeam?.abbrev || "",
      awayAbbrev: game.awayTeam?.abbrev || "",
      state,
    };
  }

  return {
    winner: "TIE",
    homeAbbrev: game.homeTeam?.abbrev || "",
    awayAbbrev: game.awayTeam?.abbrev || "",
    state,
  };
};

const normalizePredictionText = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const isPredictionMatch = (userPrediction, winningPlay) => {
  const user = normalizePredictionText(userPrediction);
  const actual = normalizePredictionText(winningPlay);

  if (!user || !actual) return false;
  return actual.includes(user) || user.includes(actual);
};

const isOvertimePickEntry = (entry) => {
  const itemId = String(entry?.item_id || "");
  return entry?.pick_type === "overtime-play" ||
    (entry?.pick_type === "game" && itemId.startsWith(OT_LEGACY_ITEM_PREFIX));
};

const getOvertimeGameIdFromEntry = (entry) => {
  const itemId = String(entry?.item_id || "");
  if (itemId.startsWith(OT_LEGACY_ITEM_PREFIX)) {
    return itemId.slice(OT_LEGACY_ITEM_PREFIX.length);
  }
  return itemId;
};

const parseStructuredOvertimePick = (pickChoice) => {
  const raw = String(pickChoice || "");

  if (raw.startsWith("OT|")) {
    const parts = raw.split("|").slice(1);
    if (parts.length >= 2) {
      const [awayPart, homePart] = parts;
      const [awayTeam, awayPlayerId] = awayPart.split(":");
      const [homeTeam, homePlayerId] = homePart.split(":");
      if (awayTeam && awayPlayerId && homeTeam && homePlayerId) {
        return {
          awayTeam: String(awayTeam).toUpperCase(),
          awayPlayerId: String(awayPlayerId),
          homeTeam: String(homeTeam).toUpperCase(),
          homePlayerId: String(homePlayerId),
          awayPlayer: String(awayPlayerId),
          homePlayer: String(homePlayerId),
        };
      }
    }
  }

  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;

    if (parsed.awayPlayer && parsed.homePlayer) {
      return {
        awayTeam: String(parsed.awayTeam || "").toUpperCase(),
        awayPlayer: String(parsed.awayPlayer || ""),
        awayPlayerId: parsed.awayPlayerId ? String(parsed.awayPlayerId) : "",
        homeTeam: String(parsed.homeTeam || "").toUpperCase(),
        homePlayer: String(parsed.homePlayer || ""),
        homePlayerId: parsed.homePlayerId ? String(parsed.homePlayerId) : "",
      };
    }
  } catch {
    return null;
  }

  return null;
};

const formatOvertimePickLabel = (pickChoice) => {
  const structured = parseStructuredOvertimePick(pickChoice);
  if (!structured) {
    return String(pickChoice || "");
  }

  const awayLabel = structured.awayPlayer || structured.awayPlayerId || "Unknown";
  const homeLabel = structured.homePlayer || structured.homePlayerId || "Unknown";

  return `${structured.awayTeam}: ${awayLabel} | ${structured.homeTeam}: ${homeLabel}`;
};

const isOvertimePickCorrect = (pickChoice, overtimeOutcome) => {
  const structured = parseStructuredOvertimePick(pickChoice);
  if (!structured) {
    return isPredictionMatch(pickChoice, overtimeOutcome?.winningPlay || "");
  }

  const winner = String(overtimeOutcome?.winner || "").toUpperCase();
  if (!winner) return false;

  if (structured.awayPlayerId && structured.homePlayerId && overtimeOutcome?.scorerId) {
    const predictedWinnerScorerId =
      winner === structured.awayTeam
        ? structured.awayPlayerId
        : winner === structured.homeTeam
        ? structured.homePlayerId
        : "";

    if (predictedWinnerScorerId) {
      return String(predictedWinnerScorerId) === String(overtimeOutcome.scorerId);
    }
  }

  const predictedWinnerScorer =
    winner === structured.awayTeam
      ? structured.awayPlayer
      : winner === structured.homeTeam
      ? structured.homePlayer
      : "";

  return isPredictionMatch(predictedWinnerScorer, overtimeOutcome?.scorerName || "");
};

const fetchOvertimeOutcomeFromApi = async (gameId) => {
  const response = await fetch(
    `https://friendly-bets-back-end.vercel.app/api/gamecenter/${gameId}/play-by-play`
  );

  if (!response.ok) {
    throw new Error(`Failed fetching overtime outcome for ${gameId}`);
  }

  const game = await response.json();
  const state = String(game.gameState || "").toUpperCase();
  const home = Number(game.homeTeam?.score || 0);
  const away = Number(game.awayTeam?.score || 0);
  const winner =
    home > away
      ? game.homeTeam?.abbrev || null
      : away > home
      ? game.awayTeam?.abbrev || null
      : null;
  const overtimeGoals = (game.plays || []).filter(
    (play) => play?.typeDescKey === "goal" && play?.periodDescriptor?.periodType === "OT"
  );
  const overtimeWinnerGoal = overtimeGoals[overtimeGoals.length - 1] || null;
  const scoringPlayerId = overtimeWinnerGoal?.details?.scoringPlayerId;
  const scorer = (game.rosterSpots || []).find((player) => player.playerId === scoringPlayerId);
  const scorerName = scorer
    ? `${scorer.firstName?.default || ""} ${scorer.lastName?.default || ""}`.trim()
    : "";
  const shotType = overtimeWinnerGoal?.details?.shotType || "goal";
  const winningPlay = overtimeWinnerGoal
    ? `${scorerName || "Unknown scorer"} ${shotType} goal`.trim()
    : "";

  return {
    gameId: String(gameId),
    state,
    winner,
    resolved: Boolean((state === "OFF" || state === "FINAL") && overtimeWinnerGoal),
    awayAbbrev: game.awayTeam?.abbrev || "",
    homeAbbrev: game.homeTeam?.abbrev || "",
    winningPlay,
    scorerId: scoringPlayerId ? String(scoringPlayerId) : "",
    scorerName,
    shotType,
  };
};

const NhlPlayoffsHub = () => {
  const { theme } = useTheme();
  const [activeTab, setActiveTab] = useState("series");
  const [userId, setUserId] = useState("");
  const [username, setUsername] = useState("");
  const [loadingUser, setLoadingUser] = useState(true);
  const [todayGames, setTodayGames] = useState([]);
  const [allPicks, setAllPicks] = useState([]);
  const [savingKey, setSavingKey] = useState("");
  const [infoMessage, setInfoMessage] = useState("");
  const [leaderboardSource, setLeaderboardSource] = useState("global");
  const [resolvedGameResults, setResolvedGameResults] = useState({});
  const [checkingApiResults, setCheckingApiResults] = useState(false);
  const [seriesOptions, setSeriesOptions] = useState([]);
  const [resolvedSeriesWinners, setResolvedSeriesWinners] = useState({});
  const [dbSeriesWinners, setDbSeriesWinners] = useState({});
  const [loadingSeries, setLoadingSeries] = useState(false);
  const [lockingSeriesRound, setLockingSeriesRound] = useState("");
  const [overtimeGames, setOvertimeGames] = useState([]);
  const [checkingOvertimeGames, setCheckingOvertimeGames] = useState(false);
  const [overtimeRosterByGame, setOvertimeRosterByGame] = useState({});
  const [overtimeSelectionByGame, setOvertimeSelectionByGame] = useState({});
  const [resolvedOvertimeOutcomes, setResolvedOvertimeOutcomes] = useState({});
  const [showOvertimeModal, setShowOvertimeModal] = useState(false);
  const [dismissedOvertimeGameIds, setDismissedOvertimeGameIds] = useState([]);
  const [toast, setToast] = useState({ message: "", visible: false });

  useEffect(() => {
    const loadUser = async () => {
      setLoadingUser(true);
      try {
        const { data: sessionData } = await supabase.auth.getSession();
        const authId = sessionData?.session?.user?.id;
        if (!authId) {
          setLoadingUser(false);
          return;
        }

        setUserId(authId);

        const { data: profile } = await supabase
          .from("users")
          .select("username")
          .eq("public_user_id", authId)
          .single();

        const resolvedUsername = profile?.username || "Player";
        setUsername(resolvedUsername);

      } catch (error) {
        console.error("Failed loading playoff user context:", error);
      } finally {
        setLoadingUser(false);
      }
    };

    loadUser();
  }, []);

  // Load server-authoritative series winners from Supabase on mount.
  useEffect(() => {
    const loadDbSeriesWinners = async () => {
      try {
        const { data, error } = await supabase
          .from("nhl_playoff_series_results")
          .select("series_id, winner_abbrev")
          .eq("season_year", CURRENT_PLAYOFF_YEAR);

        if (error) throw error;

        const map = {};
        (data || []).forEach((row) => {
          if (row.series_id && row.winner_abbrev) {
            map[row.series_id] = row.winner_abbrev;
          }
        });
        setDbSeriesWinners(map);
      } catch {
        // Table may not exist yet; silently fall back to ESPN-derived winners.
      }
    };

    loadDbSeriesWinners();
  }, []);

  useEffect(() => {
    const loadTodayGames = async () => {
      try {
        const response = await fetch("https://friendly-bets-back-end.vercel.app/api/now");
        const data = await response.json();

        const today = toISODate();
        const todayBlock = (data?.gameWeek || []).find((day) => day.date === today);
        setTodayGames(todayBlock?.games || []);
      } catch (error) {
        console.error("Failed fetching playoff game slate:", error);
        setTodayGames([]);
      }
    };

    loadTodayGames();
  }, []);

  useEffect(() => {
    const detectOvertimeGames = async () => {
      setCheckingOvertimeGames(true);

      try {
        const response = await fetch("https://friendly-bets-back-end.vercel.app/api/now");
        const data = await response.json();
        const today = toISODate();
        const todayBlock = (data?.gameWeek || []).find((day) => day.date === today);
        const games = todayBlock?.games || [];

        const inOvertime = games.filter((game) => {
          const periodType = String(game?.periodDescriptor?.periodType || "").toUpperCase();
          const state = String(game?.gameState || "").toUpperCase();
          return periodType === "OT" && state !== "FINAL" && state !== "OFF";
        });

        setOvertimeGames(inOvertime);
        setDismissedOvertimeGameIds((prev) => {
          const activeIds = new Set(inOvertime.map((game) => String(game.id)));
          return prev.filter((gameId) => activeIds.has(gameId));
        });
      } catch (error) {
        console.error("Failed checking overtime slate:", error);
        setOvertimeGames([]);
      } finally {
        setCheckingOvertimeGames(false);
      }
    };

    detectOvertimeGames();
    const intervalId = setInterval(detectOvertimeGames, 45000);
    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    if (overtimeGames.length === 0) {
      setShowOvertimeModal(false);
      return;
    }

    const hasUndismissedGame = overtimeGames.some(
      (game) => !dismissedOvertimeGameIds.includes(String(game.id))
    );

    if (hasUndismissedGame) {
      setShowOvertimeModal(true);
    }
  }, [overtimeGames, dismissedOvertimeGameIds]);

  useEffect(() => {
    const loadOvertimeRosters = async () => {
      if (overtimeGames.length === 0) {
        setOvertimeRosterByGame({});
        return;
      }

      const rows = await Promise.all(
        overtimeGames.map(async (game) => {
          const gameId = String(game.id);

          try {
            const response = await fetch(
              `https://friendly-bets-back-end.vercel.app/api/gamecenter/${gameId}/play-by-play`
            );

            if (!response.ok) {
              throw new Error(`Failed loading roster for ${gameId}`);
            }

            const data = await response.json();
            const awayAbbrev = String(data?.awayTeam?.abbrev || game?.awayTeam?.abbrev || "AWY");
            const homeAbbrev = String(data?.homeTeam?.abbrev || game?.homeTeam?.abbrev || "HME");
            const awayTeamId = Number(data?.awayTeam?.id || 0);
            const homeTeamId = Number(data?.homeTeam?.id || 0);
            const roster = Array.isArray(data?.rosterSpots) ? data.rosterSpots : [];

            const toPlayer = (player) => ({
              id: String(player?.playerId || ""),
              name: `${player?.firstName?.default || ""} ${player?.lastName?.default || ""}`.trim(),
            });

            const awayPlayers = roster
              .filter((player) => Number(player?.teamId || 0) === awayTeamId)
              .map(toPlayer)
              .filter((player) => player.id && player.name)
              .sort((a, b) => a.name.localeCompare(b.name));

            const homePlayers = roster
              .filter((player) => Number(player?.teamId || 0) === homeTeamId)
              .map(toPlayer)
              .filter((player) => player.id && player.name)
              .sort((a, b) => a.name.localeCompare(b.name));

            return {
              gameId,
              data: {
                awayAbbrev,
                homeAbbrev,
                awayPlayers,
                homePlayers,
              },
            };
          } catch {
            return {
              gameId,
              data: {
                awayAbbrev: String(game?.awayTeam?.abbrev || "AWY"),
                homeAbbrev: String(game?.homeTeam?.abbrev || "HME"),
                awayPlayers: [],
                homePlayers: [],
              },
            };
          }
        })
      );

      const nextMap = {};
      rows.forEach((row) => {
        nextMap[row.gameId] = row.data;
      });
      setOvertimeRosterByGame(nextMap);
    };

    loadOvertimeRosters();
  }, [overtimeGames]);

  useEffect(() => {
    const loadSeriesFromEspn = async () => {
      setLoadingSeries(true);
      try {
        const today = new Date();
        const offsets = Array.from({ length: 46 }, (_, index) => index - 38);

        const responses = await Promise.all(
          offsets.map((offset) => {
            const d = toESPNDate(addDays(today, offset));
            return fetch(
              `https://site.api.espn.com/apis/site/v2/sports/hockey/nhl/scoreboard?seasontype=3&dates=${d}`
            )
              .then((res) => (res.ok ? res.json() : null))
              .catch(() => null);
          })
        );

        const seriesById = {};

        responses.forEach((payload) => {
          (payload?.events || []).forEach((event) => {
            const eventYear = new Date(event?.date || "").getFullYear();
            const seasonType = Number(event?.season?.type || 0);
            if (eventYear !== CURRENT_PLAYOFF_YEAR || seasonType !== 3) {
              return;
            }

            const competition = event?.competitions?.[0];
            const competitors = competition?.competitors || [];

            if (competitors.length < 2) {
              return;
            }

            const series = competition?.series;
            const home = competitors.find((c) => c.homeAway === "home") || competitors[0];
            const away = competitors.find((c) => c.homeAway === "away") || competitors[1];
            const homeAbbrev = home?.team?.abbreviation;
            const awayAbbrev = away?.team?.abbreviation;
            if (!homeAbbrev || !awayAbbrev) {
              return;
            }

            const teamIds = [home?.team?.id, away?.team?.id].filter(Boolean).sort().join("-");
            const round = competition?.type?.abbreviation || "PLAYOFF";
            const seriesId = `${round}-${teamIds || `${awayAbbrev}-${homeAbbrev}`}`;

            const teamA = awayAbbrev;
            const teamB = homeAbbrev;
            const label = `${round} • ${teamA} vs ${teamB}`;

            const existing = seriesById[seriesId];
            const summary = series?.summary || "";
            const summaryWinner = inferWinnerFromSummary(summary, [
              { abbrev: homeAbbrev, team: home?.team },
              { abbrev: awayAbbrev, team: away?.team },
            ]);
            const nextRecord = {
              id: seriesId,
              label,
              teams: [teamA, teamB],
              summary,
              completed: false,
              winnerAbbrev: null,
            };

            const winsByTeamId = {};
            const winnerByTeamId = {};
            (series?.competitors || []).forEach((competitor) => {
              winsByTeamId[String(competitor.id)] = Number(competitor.wins || 0);
              winnerByTeamId[String(competitor.id)] = Boolean(competitor.winner);
            });

            const homeWins = winsByTeamId[String(home?.team?.id)] || 0;
            const awayWins = winsByTeamId[String(away?.team?.id)] || 0;
            const homeWonEvent = Boolean(home?.winner);
            const awayWonEvent = Boolean(away?.winner);
            const clinchedByWins = homeWins >= 4 || awayWins >= 4;
            const isCompleted = Boolean(series?.completed || clinchedByWins || summaryWinner);
            nextRecord.completed = isCompleted;

            if (isCompleted) {
              const homeMarkedWinner = winnerByTeamId[String(home?.team?.id)];
              const awayMarkedWinner = winnerByTeamId[String(away?.team?.id)];

              if (homeMarkedWinner && !awayMarkedWinner) {
                nextRecord.winnerAbbrev = homeAbbrev;
              } else if (awayMarkedWinner && !homeMarkedWinner) {
                nextRecord.winnerAbbrev = awayAbbrev;
              } else if (homeWins > awayWins) {
                nextRecord.winnerAbbrev = homeAbbrev;
              } else if (awayWins > homeWins) {
                nextRecord.winnerAbbrev = awayAbbrev;
              } else if (homeWonEvent && !awayWonEvent) {
                nextRecord.winnerAbbrev = homeAbbrev;
              } else if (awayWonEvent && !homeWonEvent) {
                nextRecord.winnerAbbrev = awayAbbrev;
              } else if (summaryWinner) {
                nextRecord.winnerAbbrev = summaryWinner;
              }
            }

            // Always prefer the most recent event data (responses are in chronological
            // order so last write wins), but never overwrite a completed series with
            // an incomplete one — a completed record is final.
            if (!existing || !existing.completed || nextRecord.completed) {
              seriesById[seriesId] = nextRecord;
            }
          });
        });

        const rows = Object.values(seriesById).sort((a, b) => a.label.localeCompare(b.label));
        setSeriesOptions(rows);

        // Keep winners sticky after ESPN no longer returns older completed series,
        // but clear stale winners for currently active (not completed) series.
        const latestResolved = {};
        const unresolvedNormalizedKeys = new Set();
        rows.forEach((series) => {
          if (series.winnerAbbrev) {
            latestResolved[series.id] = series.winnerAbbrev;
          } else {
            unresolvedNormalizedKeys.add(normalizeSeriesKey(series.id));
          }
        });

        setResolvedSeriesWinners((prev) => {
          const pruned = {};
          Object.entries(prev).forEach(([seriesId, winner]) => {
            const normalized = normalizeSeriesKey(seriesId);
            if (!unresolvedNormalizedKeys.has(normalized)) {
              pruned[seriesId] = winner;
            }
          });

          return { ...pruned, ...latestResolved };
        });

        // Persist newly confirmed winners to Supabase so scoring is stable
        // across browsers and sessions, independent of ESPN data fluctuations.
        if (Object.keys(latestResolved).length > 0) {
          const labeledRows = Object.entries(latestResolved).map(([seriesId, winner]) => ({
            season_year: CURRENT_PLAYOFF_YEAR,
            series_id: seriesId,
            series_label: seriesById[seriesId]?.label || "",
            winner_abbrev: winner,
          }));

          supabase
            .from("nhl_playoff_series_results")
            .upsert(labeledRows, { onConflict: "season_year,series_id" })
            .then(({ error }) => {
              if (error) {
                console.warn("Could not persist series winners to Supabase:", error.message);
              } else {
                setDbSeriesWinners((current) => ({ ...current, ...latestResolved }));
              }
            });
        }
      } catch (error) {
        console.error("Failed to load playoff series from ESPN:", error);
        setSeriesOptions([]);
      } finally {
        setLoadingSeries(false);
      }
    };

    loadSeriesFromEspn();
  }, []);

  const refreshPicks = async (currentUserId) => {
    const id = currentUserId || userId;
    if (!id) return;

    try {
      const { data, error } = await supabase
        .from("nhl_playoff_picks")
        .select("user_id, username, pick_type, item_id, pick_choice, pick_date, created_at, locked_at");

      if (error) {
        throw error;
      }

      setLeaderboardSource("global");
      const filtered = (Array.isArray(data) ? data : []).filter(isPickInCurrentPlayoffYear);
      setAllPicks(filtered);
    } catch (error) {
      setInfoMessage(
        "Could not load picks from Supabase. Please check database access or table schema."
      );
    }
  };

  useEffect(() => {
    if (!userId) return;
    refreshPicks(userId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const seriesRoundLocks = useMemo(() => {
    const byRound = {};

    allPicks
      .filter((pick) => pick.user_id === userId && pick.pick_type === "series")
      .forEach((pick) => {
        const roundKey = getRoundKeyFromSeriesId(pick.item_id);
        if (!roundKey) return;

        if (!byRound[roundKey]) {
          byRound[roundKey] = { count: 0, lockedCount: 0, allLocked: false };
        }

        byRound[roundKey].count += 1;
        if (Boolean(pick.locked_at)) {
          byRound[roundKey].lockedCount += 1;
        }
      });

    Object.keys(byRound).forEach((roundKey) => {
      const row = byRound[roundKey];
      row.allLocked = row.count > 0 && row.lockedCount === row.count;
    });

    return byRound;
  }, [allPicks, userId]);

  const availableRoundKeys = useMemo(() => {
    const set = new Set();
    seriesOptions.forEach((series) => {
      const roundKey = getRoundKeyFromSeriesId(series.id);
      if (roundKey) set.add(roundKey);
    });

    return Array.from(set).sort((a, b) => {
      const orderDiff = getRoundOrder(a) - getRoundOrder(b);
      if (orderDiff !== 0) return orderDiff;
      return a.localeCompare(b);
    });
  }, [seriesOptions]);

  useEffect(() => {
    const resolvePickedGames = async () => {
      const pickedGameIds = Array.from(
        new Set(
          allPicks
            .filter(
              (pick) =>
                pick.pick_type === "game" &&
                !String(pick.item_id || "").startsWith(OT_LEGACY_ITEM_PREFIX)
            )
            .map((pick) => String(pick.item_id))
        )
      );

      if (!pickedGameIds.length) {
        setResolvedGameResults({});
        return;
      }

      setCheckingApiResults(true);
      try {
        const results = await Promise.all(
          pickedGameIds.map(async (gameId) => {
            try {
              const result = await fetchGameResultFromApi(gameId);
              return { gameId, result };
            } catch (error) {
              console.warn("Failed resolving game result:", gameId, error);
              return { gameId, result: null };
            }
          })
        );

        const resultMap = {};
        results.forEach(({ gameId, result }) => {
          if (result) {
            resultMap[gameId] = result;
          }
        });
        setResolvedGameResults(resultMap);
      } finally {
        setCheckingApiResults(false);
      }
    };

    resolvePickedGames();
  }, [allPicks]);

  useEffect(() => {
    const resolveOvertimePicks = async () => {
      const overtimeGameIds = Array.from(
        new Set(
          allPicks
            .filter((pick) => isOvertimePickEntry(pick))
            .map((pick) => getOvertimeGameIdFromEntry(pick))
        )
      );

      if (!overtimeGameIds.length) {
        setResolvedOvertimeOutcomes({});
        return;
      }

      try {
        const outcomes = await Promise.all(
          overtimeGameIds.map(async (gameId) => {
            try {
              const result = await fetchOvertimeOutcomeFromApi(gameId);
              return { gameId, result };
            } catch (error) {
              console.warn("Failed resolving overtime result:", gameId, error);
              return { gameId, result: null };
            }
          })
        );

        const resultMap = {};
        outcomes.forEach(({ gameId, result }) => {
          if (result) {
            resultMap[gameId] = result;
          }
        });
        setResolvedOvertimeOutcomes(resultMap);
      } catch (error) {
        console.warn("Unable to resolve overtime outcomes:", error);
      }
    };

    resolveOvertimePicks();
  }, [allPicks]);

  const userSeriesPicks = useMemo(() => {
    const map = {};
    allPicks
      .filter((entry) => entry.user_id === userId && entry.pick_type === "series")
      .forEach((entry) => {
        map[entry.item_id] = entry.pick_choice;
      });
    return map;
  }, [allPicks, userId]);

  const userSeriesPickLocks = useMemo(() => {
    const map = {};
    allPicks
      .filter((entry) => entry.user_id === userId && entry.pick_type === "series")
      .forEach((entry) => {
        map[entry.item_id] = Boolean(entry.locked_at);
      });
    return map;
  }, [allPicks, userId]);

  const userGamePicks = useMemo(() => {
    const map = {};
    allPicks
      .filter(
        (entry) =>
          entry.user_id === userId &&
          entry.pick_type === "game" &&
          !String(entry.item_id || "").startsWith(OT_LEGACY_ITEM_PREFIX)
      )
      .forEach((entry) => {
        map[entry.item_id] = entry.pick_choice;
      });
    return map;
  }, [allPicks, userId]);

  const userOvertimePicks = useMemo(() => {
    const map = {};
    allPicks
      .filter((entry) => entry.user_id === userId && isOvertimePickEntry(entry))
      .forEach((entry) => {
        map[getOvertimeGameIdFromEntry(entry)] = entry.pick_choice;
      });
    return map;
  }, [allPicks, userId]);

  const submitPick = async ({ pickType, itemId, choice }) => {
    if (!userId) {
      setInfoMessage("Please log in to place playoff picks.");
      return;
    }

    if (pickType === "series") {
      const existingChoice = userSeriesPicks[itemId] || null;
      const existingLocked = Boolean(userSeriesPickLocks[itemId]);

      if (existingLocked && existingChoice && existingChoice !== choice) {
        setInfoMessage(
          "This series pick is locked and cannot be changed."
        );
        return;
      }

      // No-op for clicking the already-picked team on a locked series.
      if (existingLocked && existingChoice === choice) {
        return;
      }
    }

    const key = `${pickType}-${itemId}`;
    setSavingKey(key);
    setInfoMessage("");

    const payload = {
      user_id: userId,
      username: username || "Player",
      pick_type: pickType,
      item_id: String(itemId),
      pick_choice: choice,
      pick_date: toISODate(),
      // Keep picks editable until user explicitly clicks the round lock button.
      // This guards against DB schemas where locked_at may have a default value.
      locked_at: null,
    };

    const compatibilityPayload =
      pickType === "overtime-play"
        ? {
            ...payload,
            pick_type: "game",
            item_id: `${OT_LEGACY_ITEM_PREFIX}${String(itemId)}`,
          }
        : null;
    const writePayload = compatibilityPayload || payload;

    try {
      const { error } = await supabase
        .from("nhl_playoff_picks")
        .upsert(writePayload, { onConflict: "user_id,pick_type,item_id" });

      if (error) {
        throw error;
      }

      setLeaderboardSource("global");
      await refreshPicks(userId);
    } catch (error) {
      console.error("Failed saving playoff pick to Supabase:", error);
      setInfoMessage("Could not save pick to Supabase.");
    } finally {
      setSavingKey("");
    }
  };

  const showToast = useCallback((message) => {
    setToast({ message, visible: true });
    setTimeout(() => setToast({ message: "", visible: false }), 3000);
  }, []);

  const handleLockSeriesPicks = async (roundKey) => {
    if (!userId) {
      setInfoMessage("Please log in to lock your series picks.");
      return;
    }

    if (!roundKey) {
      return;
    }

    if (seriesRoundLocks[roundKey]?.allLocked) {
      return;
    }

    const hasSeriesPick = allPicks.some(
      (pick) =>
        pick.user_id === userId &&
        pick.pick_type === "series" &&
        getRoundKeyFromSeriesId(pick.item_id) === roundKey
    );

    if (!hasSeriesPick) {
      setInfoMessage(`Make at least one ${getRoundLabel(roundKey)} pick before locking.`);
      return;
    }

    const confirmed = window.confirm(
      `Lock ${getRoundLabel(roundKey)} picks? You will not be able to change them afterward.`
    );

    if (!confirmed) {
      return;
    }

    setLockingSeriesRound(roundKey);
    try {
      const { error } = await supabase
        .from("nhl_playoff_picks")
        .update({ locked_at: new Date().toISOString() })
        .eq("user_id", userId)
        .eq("pick_type", "series")
        .like("item_id", `${roundKey}-%`)
        .is("locked_at", null);

      if (error) {
        throw error;
      }

      await refreshPicks(userId);
      showToast(`🔒 ${getRoundLabel(roundKey)} picks locked in! You can no longer edit them.`);
    } catch (error) {
      console.error("Failed to lock series picks:", error);
      setInfoMessage(`Could not lock ${getRoundLabel(roundKey)} picks right now. Please try again.`);
    } finally {
      setLockingSeriesRound("");
    }
  };

  const todayGameWinnerMap = useMemo(() => buildGameWinnerMap(todayGames), [todayGames]);
  const todayGameIdSet = useMemo(
    () => new Set(todayGames.map((game) => String(game.id))),
    [todayGames]
  );
  const todayGameById = useMemo(() => {
    const map = {};
    todayGames.forEach((game) => {
      map[String(game.id)] = game;
    });
    return map;
  }, [todayGames]);

  const getWinnerByGameId = useCallback(
    (gameId) => resolvedGameResults[gameId]?.winner || todayGameWinnerMap[gameId] || null,
    [resolvedGameResults, todayGameWinnerMap]
  );

  const seriesWinnerMap = useMemo(() => {
    // Priority: Supabase DB (authoritative) > ESPN live > in-memory resolved winners
    const map = { ...resolvedSeriesWinners };
    seriesOptions.forEach((series) => {
      if (series.winnerAbbrev) {
        map[series.id] = series.winnerAbbrev;
      }
    });
    // DB entries override everything — they were written from a confirmed ESPN result
    // and survive ESPN feed changes or cache flushes.
    Object.entries(dbSeriesWinners).forEach(([seriesId, winner]) => {
      map[seriesId] = winner;
    });
    return map;
  }, [seriesOptions, resolvedSeriesWinners, dbSeriesWinners]);

  const normalizedSeriesWinnerMap = useMemo(() => {
    const map = {};
    Object.entries(seriesWinnerMap).forEach(([seriesId, winner]) => {
      const key = normalizeSeriesKey(seriesId);
      if (key && winner) {
        map[key] = winner;
      }
    });
    return map;
  }, [seriesWinnerMap]);

  const getSeriesWinnerByPickId = useCallback(
    (seriesId) => {
      const exact = seriesWinnerMap[seriesId];
      if (exact) return exact;

      const normalized = normalizeSeriesKey(seriesId);
      return normalized ? normalizedSeriesWinnerMap[normalized] || null : null;
    },
    [seriesWinnerMap, normalizedSeriesWinnerMap]
  );

  const seriesLeaderboardRows = useMemo(() => {
    const byUser = {};
    const picksByUserAndSeries = {};

    allPicks
      .filter((pick) => pick.pick_type === "series")
      .forEach((pick) => {
        const id = pick.user_id || "unknown";
        if (!byUser[id]) {
          byUser[id] = {
            user_id: id,
            username: pick.username || id,
            correct: 0,
            resolved: 0,
            totalPicks: 0,
          };
        }

        const normalizedSeriesId = normalizeSeriesKey(pick.item_id) || String(pick.item_id);
        if (!picksByUserAndSeries[id]) {
          picksByUserAndSeries[id] = {};
        }

        const existingPick = picksByUserAndSeries[id][normalizedSeriesId];
        const pickTime = new Date(pick.created_at || pick.pick_date || 0).getTime();
        const existingTime = existingPick
          ? new Date(existingPick.created_at || existingPick.pick_date || 0).getTime()
          : -Infinity;

        if (!existingPick || pickTime >= existingTime) {
          picksByUserAndSeries[id][normalizedSeriesId] = pick;
        }
      });

    Object.entries(picksByUserAndSeries).forEach(([uid, picksBySeries]) => {
      Object.values(picksBySeries).forEach((pick) => {
        byUser[uid].totalPicks += 1;

        const winner = getSeriesWinnerByPickId(pick.item_id);
        if (winner) {
          byUser[uid].resolved += 1;
          if (winner === pick.pick_choice) {
            byUser[uid].correct += 1;
          }
        }
      });
    });

    return Object.values(byUser)
      .sort((a, b) => {
        if (b.correct !== a.correct) return b.correct - a.correct;
        return b.resolved - a.resolved;
      })
      .slice(0, 20);
  }, [allPicks, getSeriesWinnerByPickId]);

  const gameLeaderboardRows = useMemo(() => {
    const byUser = {};

    allPicks
      .filter(
        (pick) =>
          pick.pick_type === "game" &&
          !String(pick.item_id || "").startsWith(OT_LEGACY_ITEM_PREFIX)
      )
      .forEach((pick) => {
      const id = pick.user_id || "unknown";
      if (!byUser[id]) {
        byUser[id] = {
          user_id: id,
          username: pick.username || id,
          correct: 0,
          resolved: 0,
          totalPicks: 0,
        };
      }

      byUser[id].totalPicks += 1;

      const winner = getWinnerByGameId(pick.item_id);
      if (winner) {
        byUser[id].resolved += 1;
        if (winner === pick.pick_choice) {
          byUser[id].correct += 1;
        }
      }
    });

    return Object.values(byUser)
      .sort((a, b) => {
        if (b.correct !== a.correct) return b.correct - a.correct;
        return b.resolved - a.resolved;
      })
      .slice(0, 20);
  }, [allPicks, getWinnerByGameId]);

  const overtimeLeaderboardRows = useMemo(() => {
    const byUser = {};

    allPicks
      .filter((pick) => isOvertimePickEntry(pick))
      .forEach((pick) => {
        const id = pick.user_id || "unknown";
        if (!byUser[id]) {
          byUser[id] = {
            user_id: id,
            username: pick.username || id,
            correct: 0,
            resolved: 0,
            totalPicks: 0,
          };
        }

        byUser[id].totalPicks += 1;
        const gameId = getOvertimeGameIdFromEntry(pick);
        const outcome = resolvedOvertimeOutcomes[String(gameId)];

        if (outcome?.resolved) {
          byUser[id].resolved += 1;
          if (isOvertimePickCorrect(pick.pick_choice, outcome)) {
            byUser[id].correct += 1;
          }
        }
      });

    return Object.values(byUser)
      .sort((a, b) => {
        if (b.correct !== a.correct) return b.correct - a.correct;
        return b.resolved - a.resolved;
      })
      .slice(0, 20);
  }, [allPicks, resolvedOvertimeOutcomes]);

  const userPreviousGamePicks = useMemo(() => {
    return allPicks
      .filter(
        (entry) =>
          entry.user_id === userId &&
          entry.pick_type === "game" &&
          !String(entry.item_id || "").startsWith(OT_LEGACY_ITEM_PREFIX) &&
          !todayGameIdSet.has(String(entry.item_id))
      )
      .sort((a, b) => {
        const aTime = new Date(a.created_at || a.pick_date || 0).getTime();
        const bTime = new Date(b.created_at || b.pick_date || 0).getTime();
        return bTime - aTime;
      });
  }, [allPicks, userId, todayGameIdSet]);

  // All unique teams in the current playoff field, derived from series
  const playoffTeams = useMemo(() => {
    const set = new Set();
    seriesOptions.forEach((s) => s.teams.forEach((t) => set.add(t)));
    return Array.from(set).sort();
  }, [seriesOptions]);

  const userChampionPick = useMemo(
    () =>
      allPicks.find(
        (p) => p.user_id === userId && p.pick_type === "champion" && p.item_id === "stanley-cup"
      )?.pick_choice || null,
    [allPicks, userId]
  );

  const communityChampionPicks = useMemo(
    () =>
      allPicks
        .filter((p) => p.pick_type === "champion" && p.item_id === "stanley-cup")
        .map((p) => ({ username: p.username || "Player", pick_choice: p.pick_choice })),
    [allPicks]
  );

  // Auto-detect Stanley Cup champion from ESPN series data.
  // ESPN uses round abbreviation "F" (or "SCF") for the Stanley Cup Final.
  // Priority: ESPN completed final series → Supabase system pick → null (pending).
  const detectedChampion = useMemo(() => {
    const FINAL_ROUNDS = new Set(["F", "SCF", "FINAL", "CUP", "CUP-FINAL"]);
    const finalSeries = seriesOptions.find(
      (s) =>
        s.completed &&
        s.winnerAbbrev &&
        FINAL_ROUNDS.has(s.id.split("-")[0].toUpperCase())
    );
    return finalSeries?.winnerAbbrev || null;
  }, [seriesOptions]);

  const championLeaderboardRows = useMemo(() => {
    // Use ESPN auto-detected champion first, fall back to manual system row in Supabase.
    const systemPick = allPicks.find(
      (p) => p.pick_type === "champion" && p.item_id === "stanley-cup" && p.user_id === "system"
    );
    const champion = detectedChampion || systemPick?.pick_choice || null;

    const rows = communityChampionPicks
      .map((p) => ({
        username: p.username,
        pick: p.pick_choice,
        correct: champion ? p.pick_choice === champion : null,
      }))
      .sort((a, b) => {
        if (a.correct && !b.correct) return -1;
        if (!a.correct && b.correct) return 1;
        return a.username.localeCompare(b.username);
      });
    return { rows, champion };
  }, [allPicks, communityChampionPicks, detectedChampion]);

  // { [seriesId]: [{ username, pick_choice }] }
  const communitySeriesPicks = useMemo(() => {
    const bySeriesId = {};
    allPicks
      .filter((p) => p.pick_type === "series")
      .forEach((p) => {
        if (!bySeriesId[p.item_id]) bySeriesId[p.item_id] = [];
        bySeriesId[p.item_id].push({ username: p.username || "Player", pick_choice: p.pick_choice });
      });
    return bySeriesId;
  }, [allPicks]);

  // { [gameId]: [{ username, pick_choice }] }
  const communityGamePicks = useMemo(() => {
    const byGameId = {};
    allPicks
      .filter((p) => p.pick_type === "game")
      .filter((p) => !String(p.item_id || "").startsWith(OT_LEGACY_ITEM_PREFIX))
      .forEach((p) => {
        if (!byGameId[p.item_id]) byGameId[p.item_id] = [];
        byGameId[p.item_id].push({ username: p.username || "Player", pick_choice: p.pick_choice });
      });
    return byGameId;
  }, [allPicks]);

  // { [gameId]: [{ username, pick_choice }] }
  const communityOvertimePicks = useMemo(() => {
    const byGameId = {};
    allPicks
      .filter((p) => isOvertimePickEntry(p))
      .forEach((p) => {
        const gameId = getOvertimeGameIdFromEntry(p);
        if (!byGameId[gameId]) byGameId[gameId] = [];
        byGameId[gameId].push({ username: p.username || "Player", pick_choice: p.pick_choice });
      });
    return byGameId;
  }, [allPicks]);

  const panelClass = `playoff-hub playoff-hub--${theme}`;
  const handleCloseOvertimeModal = () => {
    const currentGameIds = overtimeGames.map((game) => String(game.id));
    setDismissedOvertimeGameIds((prev) => Array.from(new Set([...prev, ...currentGameIds])));
    setShowOvertimeModal(false);
  };

  return (
    <div className={panelClass}>
      <div className="playoff-hub__header">
        <h2>NHL Playoff Picks</h2>
        <p>
          Make picks for series winners, game winners, and O'Learys Overtime Challenge.
          These are solo picks and feed separate playoff leaderboards.
        </p>
        <p className="mb-0">Game results are auto-checked from the live game API.</p>
      </div>

      <div className="playoff-hub__tabs">
        <button
          type="button"
          className={`playoff-hub__tab ${activeTab === "series" ? "active" : ""}`}
          onClick={() => setActiveTab("series")}
        >
          Series Winner Picks
        </button>
        <button
          type="button"
          className={`playoff-hub__tab ${activeTab === "games" ? "active" : ""}`}
          onClick={() => setActiveTab("games")}
        >
          Game Win/Loss Picks
        </button>
        <button
          type="button"
          className={`playoff-hub__tab ${activeTab === "champion" ? "active" : ""}`}
          onClick={() => setActiveTab("champion")}
        >
          Stanley Cup Pick
        </button>
        <button
          type="button"
          className={`playoff-hub__tab ${activeTab === "community" ? "active" : ""}`}
          onClick={() => setActiveTab("community")}
        >
          Community Picks
        </button>
      </div>

      {infoMessage && <div className="playoff-hub__info">{infoMessage}</div>}

      {showOvertimeModal && (
        <div className="playoff-hub__modal-backdrop" role="dialog" aria-modal="true">
          <div className="playoff-hub__modal">
            <div className="playoff-hub__modal-header">
              <h3>
                <span className="playoff-hub__modal-title-text">O'Learys Overtime Challenge</span>
                <span className="playoff-hub__modal-badge">Live OT</span>
              </h3>
              <button
                type="button"
                className="playoff-hub__modal-close"
                aria-label="Close"
                onClick={handleCloseOvertimeModal}
              >
                x
              </button>
            </div>
            <p className="playoff-hub__modal-copy">
              A playoff game is in overtime. Predict the exact game-winning play before the final goal.
            </p>

            {checkingOvertimeGames && (
              <div className="playoff-hub__empty">Checking for live overtime games...</div>
            )}

            {!checkingOvertimeGames && overtimeGames.length === 0 && (
              <div className="playoff-hub__empty">No active overtime games right now.</div>
            )}

            <div className="playoff-hub__grid">
              {overtimeGames.map((game, index) => {
                const gameId = String(game.id);
                const away = game.awayTeam?.abbrev || "Away";
                const home = game.homeTeam?.abbrev || "Home";
                const savedPrediction = userOvertimePicks[gameId] || "";
                const savedStructuredPick = parseStructuredOvertimePick(savedPrediction);
                const roster = overtimeRosterByGame[gameId] || {
                  awayAbbrev: away,
                  homeAbbrev: home,
                  awayPlayers: [],
                  homePlayers: [],
                };
                const awayPlayersById = {};
                roster.awayPlayers.forEach((player) => {
                  awayPlayersById[String(player.id)] = player.name;
                });
                const homePlayersById = {};
                roster.homePlayers.forEach((player) => {
                  homePlayersById[String(player.id)] = player.name;
                });
                const awayPlayerSelection =
                  overtimeSelectionByGame[gameId]?.awayPlayer ||
                  savedStructuredPick?.awayPlayerId ||
                  "";
                const homePlayerSelection =
                  overtimeSelectionByGame[gameId]?.homePlayer ||
                  savedStructuredPick?.homePlayerId ||
                  "";
                const overtimeOutcome = resolvedOvertimeOutcomes[gameId];
                const isCorrect = overtimeOutcome?.resolved
                  ? isOvertimePickCorrect(savedPrediction, overtimeOutcome)
                  : null;

                return (
                  <div
                    key={gameId}
                    className="playoff-hub__card playoff-hub__card--ot"
                    style={{ animationDelay: `${index * 90}ms` }}
                  >
                    <div className="playoff-hub__card-label">{away} vs {home}</div>
                    <div className="playoff-hub__meta">
                      State: {game.gameState} | OT Period: {game.periodDescriptor?.number || 4}
                    </div>
                    <div className="playoff-hub__overtime-entry">
                      <select
                        className="playoff-hub__select-input"
                        value={awayPlayerSelection}
                        onChange={(event) => {
                          const nextValue = event.target.value;
                          setOvertimeSelectionByGame((prev) => ({
                            ...prev,
                            [gameId]: {
                              awayPlayer: nextValue,
                              homePlayer: prev?.[gameId]?.homePlayer || homePlayerSelection,
                            },
                          }));
                        }}
                      >
                        <option value="">Pick {roster.awayAbbrev} scorer</option>
                        {roster.awayPlayers.map((player) => (
                          <option key={`${gameId}-away-${player.id}`} value={player.id}>
                            {player.name}
                          </option>
                        ))}
                      </select>
                      <select
                        className="playoff-hub__select-input"
                        value={homePlayerSelection}
                        onChange={(event) => {
                          const nextValue = event.target.value;
                          setOvertimeSelectionByGame((prev) => ({
                            ...prev,
                            [gameId]: {
                              awayPlayer: prev?.[gameId]?.awayPlayer || awayPlayerSelection,
                              homePlayer: nextValue,
                            },
                          }));
                        }}
                      >
                        <option value="">Pick {roster.homeAbbrev} scorer</option>
                        {roster.homePlayers.map((player) => (
                          <option key={`${gameId}-home-${player.id}`} value={player.id}>
                            {player.name}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        className="playoff-hub__choice"
                        onClick={() => {
                          if (!awayPlayerSelection || !homePlayerSelection) {
                            setInfoMessage("Pick one scorer from each team before submitting O'Learys OT picks.");
                            return;
                          }

                          const structuredPick =
                            `OT|${roster.awayAbbrev}:${awayPlayerSelection}|${roster.homeAbbrev}:${homePlayerSelection}`;

                          submitPick({
                            pickType: "overtime-play",
                            itemId: gameId,
                            choice: structuredPick,
                          });
                        }}
                        disabled={
                          savingKey === `overtime-play-${gameId}` ||
                          loadingUser ||
                          roster.awayPlayers.length === 0 ||
                          roster.homePlayers.length === 0
                        }
                      >
                        {savingKey === `overtime-play-${gameId}` ? "Saving..." : "Submit OT Pick"}
                      </button>
                    </div>

                    {(roster.awayPlayers.length === 0 || roster.homePlayers.length === 0) && (
                      <div className="playoff-hub__meta">Roster still loading for this game.</div>
                    )}

                    {savedPrediction && (
                      <div className="playoff-hub__meta">
                        Your saved picks: <strong>{`${roster.awayAbbrev}: ${awayPlayersById[savedStructuredPick?.awayPlayerId] || savedStructuredPick?.awayPlayer || "Unknown"} | ${roster.homeAbbrev}: ${homePlayersById[savedStructuredPick?.homePlayerId] || savedStructuredPick?.homePlayer || "Unknown"}`}</strong>
                      </div>
                    )}

                    {overtimeOutcome?.resolved && (
                      <div className="playoff-hub__meta">
                        Winning play: <strong>{overtimeOutcome.winningPlay}</strong>{" "}
                        <span className={isCorrect ? "community-correct" : "community-wrong"}>
                          {isCorrect ? "(Correct)" : "(Missed)"}
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="playoff-hub__modal-actions">
              <button
                type="button"
                className="playoff-hub__lock-btn"
                onClick={handleCloseOvertimeModal}
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}

      {activeTab === "series" && (
        <div className="playoff-hub__grid">
          <div className="playoff-hub__lock-row">
            {availableRoundKeys.map((roundKey) => {
              const isLocked = Boolean(seriesRoundLocks[roundKey]?.allLocked);
              const isLocking = lockingSeriesRound === roundKey;
              return (
                <button
                  key={roundKey}
                  type="button"
                  className={`playoff-hub__lock-btn ${isLocked ? "is-locked" : ""}`}
                  onClick={() => handleLockSeriesPicks(roundKey)}
                  disabled={loadingUser || isLocked || isLocking}
                >
                  {isLocked
                    ? `${getRoundLabel(roundKey)} Locked`
                    : isLocking
                    ? `Locking ${getRoundLabel(roundKey)}...`
                    : `Lock ${getRoundLabel(roundKey)} Picks`}
                </button>
              );
            })}
            <small>Each round locks separately. Later rounds remain editable until you lock them.</small>
          </div>

          {loadingSeries && (
            <div className="playoff-hub__empty">Loading playoff series from ESPN...</div>
          )}

          {!loadingSeries && seriesOptions.length === 0 && (
            <div className="playoff-hub__empty">No playoff series found from ESPN right now.</div>
          )}

          {seriesOptions.map((series) => (
            <div key={series.id} className="playoff-hub__card">
              <div className="playoff-hub__card-label">{series.label}</div>
              {series.summary ? <div className="playoff-hub__meta">{series.summary}</div> : null}
              {series.completed && (
                <div className="playoff-hub__meta">Series closed. Picks are locked.</div>
              )}
              <div className="playoff-hub__choices">
                {series.teams.map((team, index) => {
                  const unresolved = isUnresolvedTeamSlot(team);
                  return (
                  <button
                    key={`${series.id}-${index}-${team}`}
                    type="button"
                    className={`playoff-hub__choice ${userSeriesPicks[series.id] === team ? "is-picked" : ""}`}
                    onClick={() => submitPick({ pickType: "series", itemId: series.id, choice: team })}
                    disabled={
                      unresolved ||
                      Boolean(userSeriesPickLocks[series.id]) ||
                      series.completed ||
                      savingKey === `series-${series.id}` ||
                      loadingUser
                    }
                  >
                    {toTeamButtonLabel(team)}
                  </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {activeTab === "games" && (
        <div className="playoff-hub__grid">
          {todayGames.length === 0 && (
            <div className="playoff-hub__empty">No NHL games available right now.</div>
          )}

          {todayGames.map((game) => {
            const gameId = String(game.id);
            const away = game.awayTeam?.abbrev;
            const home = game.homeTeam?.abbrev;
            const picked = userGamePicks[gameId];
            const winner = getWinnerByGameId(gameId);
            const resultClass =
              winner && picked
                ? winner === picked
                  ? "is-correct"
                  : "is-incorrect"
                : "";

            return (
              <div key={gameId} className={`playoff-hub__card ${resultClass}`}>
                <div className="playoff-hub__card-label">{away} vs {home}</div>
                <div className="playoff-hub__choices">
                  <button
                    type="button"
                    className={`playoff-hub__choice ${picked === away ? "is-picked" : ""}`}
                    onClick={() => submitPick({ pickType: "game", itemId: gameId, choice: away })}
                    disabled={savingKey === `game-${gameId}` || loadingUser}
                  >
                    {away}
                  </button>
                  <button
                    type="button"
                    className={`playoff-hub__choice ${picked === home ? "is-picked" : ""}`}
                    onClick={() => submitPick({ pickType: "game", itemId: gameId, choice: home })}
                    disabled={savingKey === `game-${gameId}` || loadingUser}
                  >
                    {home}
                  </button>
                </div>
                <div className="playoff-hub__meta">
                  {winner
                    ? `Final winner: ${winner}`
                    : checkingApiResults
                    ? "Checking API result..."
                    : "Pending final result"}
                </div>
              </div>
            );
          })}

          {userPreviousGamePicks.length > 0 && (
            <div className="playoff-hub__history">
              <h4 className="playoff-hub__history-title">My Previous Picks</h4>
              <div className="playoff-hub__history-list">
                {userPreviousGamePicks.map((pick) => {
                  const gameId = String(pick.item_id);
                  const apiResult = resolvedGameResults[gameId];
                  const winner = getWinnerByGameId(gameId);
                  const todayGame = todayGameById[gameId];
                  const away = apiResult?.awayAbbrev || todayGame?.awayTeam?.abbrev || "Away";
                  const home = apiResult?.homeAbbrev || todayGame?.homeTeam?.abbrev || "Home";
                  const isCorrect = winner && pick.pick_choice === winner;

                  return (
                    <div key={`${pick.user_id}-${pick.item_id}`} className="playoff-hub__history-item">
                      <div>
                        <strong>{away} vs {home}</strong>
                        <div className="playoff-hub__meta">Pick: {pick.pick_choice}</div>
                      </div>
                      <div className={`playoff-hub__history-status ${isCorrect ? "ok" : winner ? "bad" : "pending"}`}>
                        {winner ? `Result: ${winner}` : "Pending"}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === "champion" && (
        <div className="playoff-hub__champion">
          <div className="playoff-hub__champion-header">
            <h3>🏆 Who will win the Stanley Cup?</h3>
            <p>Pick the team you think will go all the way. You can change your pick any time.</p>
          </div>

          {loadingSeries && (
            <div className="playoff-hub__empty">Loading playoff teams...</div>
          )}

          {!loadingSeries && playoffTeams.length === 0 && (
            <div className="playoff-hub__empty">No playoff teams found yet. Check back soon.</div>
          )}

          <div className="playoff-hub__champion-grid">
            {playoffTeams.map((team) => (
              <button
                key={team}
                type="button"
                className={`playoff-hub__champion-btn ${
                  userChampionPick === team ? "is-picked" : ""
                }`}
                onClick={() =>
                  submitPick({ pickType: "champion", itemId: "stanley-cup", choice: team })
                }
                disabled={savingKey === "champion-stanley-cup" || loadingUser}
              >
                {team}
                {userChampionPick === team && (
                  <span className="playoff-hub__champion-check">✓ Your Pick</span>
                )}
              </button>
            ))}
          </div>

          {userChampionPick && (
            <div className="playoff-hub__champion-current">
              Your current pick: <strong>{userChampionPick}</strong>
            </div>
          )}
        </div>
      )}

      {activeTab === "community" && (
        <div className="playoff-hub__community">
          <h3 className="playoff-hub__community-section-title">Series Winner Picks</h3>
          {seriesOptions.length === 0 && (
            <div className="playoff-hub__empty">No series data loaded yet.</div>
          )}
          {seriesOptions.map((series) => {
            const userPicksList = communitySeriesPicks[series.id] || [];
            return (
              <div key={series.id} className="playoff-hub__community-card">
                <div className="playoff-hub__card-label">{series.label}</div>
                {series.summary ? <div className="playoff-hub__meta">{series.summary}</div> : null}
                {userPicksList.length === 0 ? (
                  <div className="playoff-hub__meta">No picks yet.</div>
                ) : (
                  <table className="playoff-hub__community-table">
                    <thead>
                      <tr>
                        <th>User</th>
                        <th>Pick</th>
                      </tr>
                    </thead>
                    <tbody>
                      {userPicksList.map((entry, i) => (
                        <tr key={i}>
                          <td>{entry.username}</td>
                          <td className="playoff-hub__community-pick">{entry.pick_choice}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            );
          })}

          <h3 className="playoff-hub__community-section-title">Stanley Cup Predictions</h3>
          {communityChampionPicks.length === 0 ? (
            <div className="playoff-hub__empty">No champion picks yet.</div>
          ) : (
            <div className="playoff-hub__community-card">
              <div className="playoff-hub__card-label">🏆 Stanley Cup Champion Pick</div>
              {championLeaderboardRows.champion && (
                <div className="playoff-hub__meta">
                  Champion: <strong>{championLeaderboardRows.champion}</strong>
                </div>
              )}
              <table className="playoff-hub__community-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Pick</th>
                    {championLeaderboardRows.champion && <th>Result</th>}
                  </tr>
                </thead>
                <tbody>
                  {championLeaderboardRows.rows.map((entry, i) => (
                    <tr key={i}>
                      <td>{entry.username}</td>
                      <td className="playoff-hub__community-pick">{entry.pick}</td>
                      {championLeaderboardRows.champion && (
                        <td className={entry.correct ? "community-correct" : "community-wrong"}>
                          {entry.correct ? "✓" : "✗"}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <h3 className="playoff-hub__community-section-title">Game Winner Picks</h3>
          {Object.keys(communityGamePicks).length === 0 && (
            <div className="playoff-hub__empty">No game picks to show yet.</div>
          )}
          {Object.entries(communityGamePicks).map(([gameId, userPicksList]) => {
            const apiResult = resolvedGameResults[gameId];
            const todayGame = todayGameById[gameId];
            const away = apiResult?.awayAbbrev || todayGame?.awayTeam?.abbrev || "Away";
            const home = apiResult?.homeAbbrev || todayGame?.homeTeam?.abbrev || "Home";
            const winner = getWinnerByGameId(gameId);
            return (
              <div key={gameId} className="playoff-hub__community-card">
                <div className="playoff-hub__card-label">{away} vs {home}</div>
                {winner && (
                  <div className="playoff-hub__meta">Final: {winner}</div>
                )}
                <table className="playoff-hub__community-table">
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Pick</th>
                      {winner && <th>Result</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {userPicksList.map((entry, i) => {
                      const correct = winner ? entry.pick_choice === winner : null;
                      return (
                        <tr key={i}>
                          <td>{entry.username}</td>
                          <td className="playoff-hub__community-pick">{entry.pick_choice}</td>
                          {winner && (
                            <td className={correct ? "community-correct" : "community-wrong"}>
                              {correct ? "✓" : "✗"}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            );
          })}

          <h3 className="playoff-hub__community-section-title">O'Learys Overtime Challenge Picks</h3>
          {Object.keys(communityOvertimePicks).length === 0 && (
            <div className="playoff-hub__empty">No overtime challenge picks yet.</div>
          )}
          {Object.entries(communityOvertimePicks).map(([gameId, picks]) => {
            const outcome = resolvedOvertimeOutcomes[gameId];
            const away = outcome?.awayAbbrev || todayGameById[gameId]?.awayTeam?.abbrev || "Away";
            const home = outcome?.homeAbbrev || todayGameById[gameId]?.homeTeam?.abbrev || "Home";

            return (
              <div key={`ot-${gameId}`} className="playoff-hub__community-card">
                <div className="playoff-hub__card-label">{away} vs {home}</div>
                {outcome?.resolved && (
                  <div className="playoff-hub__meta">
                    Winning play: <strong>{outcome.winningPlay}</strong>
                  </div>
                )}
                <table className="playoff-hub__community-table">
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Prediction</th>
                      {outcome?.resolved && <th>Result</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {picks.map((entry, index) => {
                      const correct = outcome?.resolved
                        ? isOvertimePickCorrect(entry.pick_choice, outcome)
                        : null;

                      return (
                        <tr key={`${gameId}-${index}`}>
                          <td>{entry.username}</td>
                          <td className="playoff-hub__community-pick">
                            {formatOvertimePickLabel(entry.pick_choice)}
                          </td>
                          {outcome?.resolved && (
                            <td className={correct ? "community-correct" : "community-wrong"}>
                              {correct ? "✓" : "✗"}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            );
          })}
        </div>
      )}

      <div className="playoff-hub__leaderboards">
        <div className="playoff-hub__leaderboard">
          <div className="playoff-hub__leaderboard-head">
            <h3>🏆 Stanley Cup Leaderboard</h3>
            <span>{leaderboardSource === "global" ? "Global" : "Local"}</span>
          </div>
          <div className="table-responsive">
            <table className="table table-striped table-sm mb-0">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Pick</th>
                  <th>Result</th>
                </tr>
              </thead>
              <tbody>
                {championLeaderboardRows.rows.length === 0 && (
                  <tr>
                    <td colSpan={3}>No champion picks yet.</td>
                  </tr>
                )}
                {championLeaderboardRows.rows.map((row, index) => (
                  <tr key={`champ-${index}`}>
                    <td>{row.username}</td>
                    <td>{row.pick}</td>
                    <td>
                      {championLeaderboardRows.champion
                        ? row.correct
                          ? <span style={{ color: "#4caf50", fontWeight: 700 }}>✓</span>
                          : <span style={{ color: "#e05555", fontWeight: 700 }}>✗</span>
                        : <span style={{ opacity: 0.5 }}>Pending</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="playoff-hub__leaderboard">
          <div className="playoff-hub__leaderboard-head">
            <h3>Series Winner Leaderboard</h3>
            <span>{leaderboardSource === "global" ? "Global" : "Local"}</span>
          </div>
          <div className="table-responsive">
            <table className="table table-striped table-sm mb-0">
              <thead>
                <tr>
                  <th>#</th>
                  <th>User</th>
                  <th>Correct</th>
                  <th>Resolved</th>
                  <th>Series Picks</th>
                </tr>
              </thead>
              <tbody>
                {seriesLeaderboardRows.length === 0 && (
                  <tr>
                    <td colSpan={5}>No series picks yet.</td>
                  </tr>
                )}
                {seriesLeaderboardRows.map((row, index) => (
                  <tr key={`series-${row.user_id}`}>
                    <td>{index + 1}</td>
                    <td>{row.username}</td>
                    <td>{row.correct}</td>
                    <td>{row.resolved}</td>
                    <td>{row.totalPicks}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="playoff-hub__leaderboard">
          <div className="playoff-hub__leaderboard-head">
            <h3>Game Winner Leaderboard</h3>
            <span>{leaderboardSource === "global" ? "Global" : "Local"}</span>
          </div>
          <div className="table-responsive">
            <table className="table table-striped table-sm mb-0">
              <thead>
                <tr>
                  <th>#</th>
                  <th>User</th>
                  <th>Correct</th>
                  <th>Resolved</th>
                  <th>Game Picks</th>
                </tr>
              </thead>
              <tbody>
                {gameLeaderboardRows.length === 0 && (
                  <tr>
                    <td colSpan={5}>No game picks yet.</td>
                  </tr>
                )}
                {gameLeaderboardRows.map((row, index) => (
                  <tr key={`game-${row.user_id}`}>
                    <td>{index + 1}</td>
                    <td>{row.username}</td>
                    <td>{row.correct}</td>
                    <td>{row.resolved}</td>
                    <td>{row.totalPicks}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="playoff-hub__leaderboard">
          <div className="playoff-hub__leaderboard-head">
            <h3>O'Learys Overtime Challenge Leaderboard</h3>
            <span>{leaderboardSource === "global" ? "Global" : "Local"}</span>
          </div>
          <div className="table-responsive">
            <table className="table table-striped table-sm mb-0">
              <thead>
                <tr>
                  <th>#</th>
                  <th>User</th>
                  <th>Correct</th>
                  <th>Resolved</th>
                  <th>OT Picks</th>
                </tr>
              </thead>
              <tbody>
                {overtimeLeaderboardRows.length === 0 && (
                  <tr>
                    <td colSpan={5}>No overtime challenge picks yet.</td>
                  </tr>
                )}
                {overtimeLeaderboardRows.map((row, index) => (
                  <tr key={`ot-${row.user_id}`}>
                    <td>{index + 1}</td>
                    <td>{row.username}</td>
                    <td>{row.correct}</td>
                    <td>{row.resolved}</td>
                    <td>{row.totalPicks}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {toast.visible && (
        <div className="playoff-hub__toast" role="status" aria-live="polite">
          {toast.message}
        </div>
      )}
    </div>
  );
};

export default NhlPlayoffsHub;
